/**
 * Feature detection → device_type para pipeline de voz / IA.
 * Whisper ONNX: se activa en desktop (UA), no por ancho de ventana.
 * Modelo (tiny/base/small) se elige on-the-fly según cores/RAM/WebGPU.
 */
import { isMobile, isTablet } from 'react-device-detect'
import { isConstrainedUiDevice } from '../utils/mobileDownloadProfile'
import { DEVICE_TYPES } from './types'

/** @typedef {'tiny'|'base'|'small'} WhisperModelTier */

const MODEL_IDS = {
  tiny: 'Xenova/whisper-tiny',
  base: 'Xenova/whisper-base',
  small: 'Xenova/whisper-small',
}

const MODEL_OVERRIDE_KEY = 'udv.voiceNotes.whisperModel'

/**
 * Override manual: tiny | base | small | auto (default).
 * @returns {WhisperModelTier|null} null = auto
 */
export function getWhisperModelOverride() {
  try {
    if (typeof localStorage === 'undefined') return null
    const v = String(localStorage.getItem(MODEL_OVERRIDE_KEY) || '')
      .trim()
      .toLowerCase()
    if (!v || v === 'auto') return null
    if (v === 'tiny' || v === 'base' || v === 'small') return v
    if (v.includes('small')) return 'small'
    if (v.includes('base')) return 'base'
    if (v.includes('tiny')) return 'tiny'
  } catch (e) {
    /* ignore */
  }
  return null
}

/**
 * Heurística de tier según hardware (sin mirar override).
 * @param {{ hardwareConcurrency: number, deviceMemory: number|null, hasWebGpu: boolean, canLocalWhisper: boolean }} cap
 * @returns {WhisperModelTier}
 */
export function pickWhisperModelTier(cap) {
  if (!cap || !cap.canLocalWhisper) return 'tiny'
  const hw = Number(cap.hardwareConcurrency) || 1
  const ram = cap.deviceMemory != null ? Number(cap.deviceMemory) : null
  const gpu = !!cap.hasWebGpu

  // small ≈ 244MB+ q8; pide máquina decente (o WebGPU + 4+ cores).
  if (hw >= 8 && (ram == null || ram >= 8)) return 'small'
  if (gpu && hw >= 4 && (ram == null || ram >= 4)) return 'small'

  // base: buen equilibrio si hay 4+ cores y ≥4 GiB reportados.
  if (hw >= 4 && (ram == null || ram >= 4)) return 'base'

  return 'tiny'
}

/**
 * @param {WhisperModelTier} tier
 * @returns {string}
 */
export function whisperModelIdForTier(tier) {
  return MODEL_IDS[tier] || MODEL_IDS.tiny
}

/**
 * @returns {{
 *   device_type: import('./types').DeviceType,
 *   hardwareConcurrency: number,
 *   deviceMemory: number|null,
 *   hasWebGpu: boolean,
 *   canLocalWhisper: boolean,
 *   whisperSkipReason: string|null,
 *   whisperModelTier: WhisperModelTier,
 *   whisperModelId: string,
 * }}
 */
export function probeDeviceCapability() {
  const hw =
    typeof navigator !== 'undefined' && navigator.hardwareConcurrency
      ? Number(navigator.hardwareConcurrency) || 1
      : 1
  let deviceMemory = null
  try {
    if (typeof navigator !== 'undefined' && navigator.deviceMemory != null) {
      deviceMemory = Number(navigator.deviceMemory)
    }
  } catch (e) {
    deviceMemory = null
  }
  let hasWebGpu = false
  try {
    hasWebGpu = !!(typeof navigator !== 'undefined' && navigator.gpu)
  } catch (e) {
    hasWebGpu = false
  }

  // Shells UI (Edge vs Studio): ancho importa. Whisper: solo UA móvil/tablet.
  const mobileUa = !!(isMobile || isTablet)
  const mobileUi = isConstrainedUiDevice()
  const device_type = mobileUi ? DEVICE_TYPES.MOBILE_PWA : DEVICE_TYPES.DESKTOP_STUDIO

  // Umbrales suaves: Chrome a menudo reporta deviceMemory=4/8; 2 GiB reportados basta.
  // No usar innerWidth: un Studio a <1024px seguía siendo desktop real.
  let whisperSkipReason = null
  let canLocalWhisper = true
  if (mobileUa) {
    canLocalWhisper = false
    whisperSkipReason = 'mobile_ua'
  } else if (hw < 2) {
    canLocalWhisper = false
    whisperSkipReason = `cpu_${hw}`
  } else if (deviceMemory != null && deviceMemory < 2) {
    canLocalWhisper = false
    whisperSkipReason = `ram_${deviceMemory}`
  }

  const override = getWhisperModelOverride()
  const whisperModelTier = canLocalWhisper
    ? override ||
      pickWhisperModelTier({
        hardwareConcurrency: hw,
        deviceMemory,
        hasWebGpu,
        canLocalWhisper,
      })
    : 'tiny'
  const whisperModelId = whisperModelIdForTier(whisperModelTier)

  // Live streaming: prefs o un escalón más liviano que final
  let liveModelTier = 'tiny'
  try {
    const liveRaw =
      typeof localStorage !== 'undefined'
        ? String(localStorage.getItem('udv.voiceNotes.whisperLiveModel') || '')
            .trim()
            .toLowerCase()
        : ''
    if (liveRaw === 'tiny' || liveRaw === 'base' || liveRaw === 'small') {
      liveModelTier = liveRaw
    } else if (canLocalWhisper) {
      if (whisperModelTier === 'small') liveModelTier = 'base'
      else liveModelTier = 'tiny'
    }
  } catch (e) {
    if (canLocalWhisper && whisperModelTier === 'small') liveModelTier = 'base'
  }
  const liveModelId = whisperModelIdForTier(liveModelTier)
  const canLiveLocalStt =
    canLocalWhisper &&
    typeof window !== 'undefined' &&
    typeof AudioWorkletNode !== 'undefined' &&
    !!(window.AudioContext || window.webkitAudioContext)

  return {
    device_type,
    hardwareConcurrency: hw,
    deviceMemory,
    hasWebGpu,
    canLocalWhisper,
    canLiveLocalStt,
    whisperSkipReason,
    whisperModelTier,
    whisperModelId,
    liveModelTier,
    liveModelId,
  }
}

/**
 * @returns {import('./types').DeviceType}
 */
export function resolveDeviceType() {
  return probeDeviceCapability().device_type
}

/**
 * Modelo a usar en esta sesión (auto o override).
 * @returns {{ tier: WhisperModelTier, modelId: string }}
 */
export function resolveWhisperModel() {
  const p = probeDeviceCapability()
  return { tier: p.whisperModelTier, modelId: p.whisperModelId }
}

export { MODEL_IDS as WHISPER_MODEL_IDS }

export default {
  probeDeviceCapability,
  resolveDeviceType,
  resolveWhisperModel,
  pickWhisperModelTier,
}
